/**
 * Panel-level error boundary.
 *
 * The shell hosts components owned by other workstreams; one of them throwing
 * must degrade that panel, not blank the application. Class component because
 * React still has no hook equivalent.
 */

import * as React from "react";

interface Props {
  /** Shown in the fallback so the user can say which panel failed. */
  label: string;
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(`[shell] ${this.props.label} crashed`, error, info.componentStack);
  }

  /** Lets the user retry after a transient failure (e.g. a missing asset). */
  private reset = () => this.setState({ error: null });

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="text-[13px] font-medium text-ads-text">{this.props.label} failed to render</p>
        <p className="max-w-md text-[11.5px] break-words text-ads-muted" data-selectable>
          {error.message}
        </p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-1 rounded border border-ads-border-strong px-2 py-1 text-[11.5px] text-ads-text hover:bg-ads-hover"
        >
          Retry
        </button>
      </div>
    );
  }
}
